/**
 * Country packs — configuración país-agnóstica de Space Academy.
 *
 * Cada academia (organization) elige un país; el pack aporta las etiquetas y
 * formatos locales (documento de identidad, RIF/tax id, prefijo telefónico,
 * formato de fecha, monedas). Venezuela-first: `ve` es el default, pero el
 * modelo es extensible — añadir un país = 1 entrada en `COUNTRY_PACKS`.
 *
 * Cada pack expone un `FeatureManifest` (forma del catálogo auto-descriptivo
 * del Diseño A/C): un `CatalogSource` de plataforma lo consumirá más adelante,
 * por eso el manifest se mantiene EXPORTABLE y con metadatos no vacíos.
 */

/**
 * Consecuencias ESTRUCTURADAS de deshabilitar una feature (FLAGS). Se declaran
 * junto al manifest y se muestran en el diálogo de confirmación de cada toggle
 * (plataforma/org/curso). Los datos SIEMPRE se preservan: apagar bloquea
 * escrituras nuevas y oculta UI, nunca borra nada.
 */
export interface FeatureImplications {
  /** Entradas de navegación / pantallas que desaparecen. */
  navHidden?: string[];
  /** Flujos de ESCRITURA bloqueados (las lecturas siguen abiertas). */
  flowsBlocked?: string[];
  /** Política de datos. Único valor: `preserved`. */
  dataPolicy?: "preserved";
}

/**
 * Metadatos auto-descriptivos de una feature/entrada de registry. Forma
 * compartida con el catálogo de features (lo consume un `CatalogSource`).
 *
 * FLAGS — grafo de dependencias:
 * - `requires` es una lista de CLÁUSULAS: `"key"` (dura), `"a|b|c"` (al menos
 *   una alternativa habilitada), sufijo `?` = dependencia blanda (compone en
 *   los gates de escritura pero no bloquea apagar el requisito).
 * - `conflicts` lista keys incompatibles con esta.
 * Espejo exacto del `FeatureDescriptor` Rust (`platform/catalog.rs`).
 */
export interface FeatureManifest {
  /** Clave estable y única (namespaced). */
  key: string;
  /** Nombre legible. */
  name: string;
  /** Descripción de una línea. */
  description: string;
  /** Alcance donde aplica la feature. */
  scope: "platform" | "org" | "course";
  /** Madurez. */
  status: "stable" | "beta" | "experimental";
  /** Cláusulas de dependencia (ver doc del interface). */
  requires?: string[];
  /** Features incompatibles. */
  conflicts?: string[];
  /** ¿Tiene enforcement server-side (`feature_gate`) en algún write-path? */
  gateable?: boolean;
  /**
   * Estado DE FÁBRICA cuando nadie la ha tocado (sin fila en `feature_flags`
   * ni override de org/curso). Ausente ⇒ `true`: las features nacen encendidas
   * porque representan capacidades ya entregadas.
   *
   * `false` describe una capacidad **construida y probada pero sin uso**: el
   * código sigue vivo y sus tests siguen corriendo, pero la plataforma no la
   * ofrece hasta que alguien la encienda a propósito (§L1 — presencial).
   */
  defaultEnabled?: boolean;
  /** Desde cuándo existe (informativo, p.ej. "2026-06"). */
  since?: string;
  /** Consecuencias de deshabilitarla (diálogo de confirmación). */
  implications?: FeatureImplications;
  /** Slug del centro de ayuda in-app (`/help/<slug>`) con la guía relacionada. */
  docsSlug?: string;
}

/**
 * Un country pack describe las convenciones locales de una academia.
 */
export interface CountryPack {
  /** ISO 3166-1 alpha-2 en minúsculas (p.ej. "ve"). */
  code: string;
  /** Nombre del país. */
  name: string;
  /** Etiqueta del documento de identidad personal (VE: "Cédula"). */
  idLabel: string;
  /** Etiqueta del identificador fiscal (VE: "RIF"). */
  taxIdLabel: string;
  /** Prefijo telefónico E.164 (VE: "+58"). */
  phonePrefix: string;
  /** Formato de fecha preferido (VE: "DD/MM/YYYY"). */
  dateFormat: string;
  /** Monedas de la academia: [primaria, ...secundarias]. */
  currencies: readonly string[];
  /** Zona horaria por defecto. */
  defaultTimezone: string;
  /** Locale por defecto (BCP-47 corto). */
  defaultLocale: string;
  /** Metadatos para el catálogo auto-descriptivo. */
  manifest: FeatureManifest;
}

/** Pack de Venezuela (default). */
export const VE_PACK: CountryPack = {
  code: "ve",
  name: "Venezuela",
  idLabel: "Cédula",
  taxIdLabel: "RIF",
  phonePrefix: "+58",
  dateFormat: "DD/MM/YYYY",
  currencies: ["USD", "VES"],
  defaultTimezone: "America/Caracas",
  defaultLocale: "es",
  manifest: {
    key: "academy.country.ve",
    name: "Venezuela",
    description:
      "Pack país de Venezuela: cédula/RIF, prefijo +58, fecha DD/MM/YYYY y doble moneda USD/VES (tasa BCV).",
    scope: "org",
    status: "stable",
  },
};

/** Registry de country packs indexado por código ISO alpha-2 (minúsculas). */
export const COUNTRY_PACKS: Readonly<Record<string, CountryPack>> = {
  ve: VE_PACK,
};

/** Código del pack por defecto (Venezuela-first). */
export const DEFAULT_COUNTRY_CODE = "ve";

/**
 * Devuelve el pack del país `code` (normaliza mayúsculas). Si no existe, cae al
 * pack por defecto (nunca lanza — el resto de la app siempre tiene etiquetas).
 */
export function getCountryPack(code: string | null | undefined): CountryPack {
  if (!code) {
    return VE_PACK;
  }
  return COUNTRY_PACKS[code.toLowerCase()] ?? VE_PACK;
}

/** Lista todos los packs disponibles. */
export function listCountryPacks(): CountryPack[] {
  return Object.values(COUNTRY_PACKS);
}

/** Manifests de todos los packs (para el catálogo de features). */
export function countryPackManifests(): FeatureManifest[] {
  return listCountryPacks().map((p) => p.manifest);
}

// ─── Doble moneda (USD/VES) ───────────────────────────────────────────────────
//
// **Aquí ya no se convierte nada.** La conversión USD→VES vive en el SERVIDOR
// (`billing::service::to_ves_minor`), que es quien tiene la tasa, la política de
// redondeo y la responsabilidad contable. Este módulo tenía un
// `convertWithRate` que multiplicaba en unidades MAYORES y dejaba redondear a
// `Intl`, mientras el servidor redondeaba a unidad menor: el mismo importe
// podía imprimirse con dos cifras según qué pantalla lo mostrara.
//
// El cliente CONSUME los campos `*VesMinor` que ya vienen convertidos (ver
// `apps/web/src/features/finance/components/money.tsx`). Si una pantalla nueva
// necesita bolívares, el campo se añade en el servidor — no se reintroduce una
// conversión de cliente aquí.
